import { useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.clawyee.com/api';

export function useSettings() {
  const { getAccessToken } = useAuth();

  const getSettings = useCallback(async () => {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}/settings`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch settings');
    }
    return data.settings;
  }, [getAccessToken]);

  const updateSettings = useCallback(async (updates) => {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}/settings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(updates),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to update settings');
    }
    return data.settings;
  }, [getAccessToken]);

  const getBilling = useCallback(async () => {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}/settings/billing`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch billing');
    }
    return data;
  }, [getAccessToken]);

  const getSubscriptions = useCallback(async () => {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}/subscriptions`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch subscriptions');
    }
    return data.subscriptions || [];
  }, [getAccessToken]);

  const createSubscription = useCallback(async (agentId) => {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}/subscriptions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ agentId }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to create subscription');
    }
    return data;
  }, [getAccessToken]);

  const cancelSubscription = useCallback(async (subscriptionId) => {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}/subscriptions`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ subscriptionId }),
    });
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to cancel subscription');
    }
    return true;
  }, [getAccessToken]);

  return {
    getSettings,
    updateSettings,
    getBilling,
    getSubscriptions,
    createSubscription,
    cancelSubscription,
  };
}
